import { useMemo } from 'react';
import type { QueryClient } from '@zg-rentals/react-query5';
import {
  CappsApplicationsWebApiHandler_getRentalApplication_queryOptions,
  useCappsApplicationsWebApiHandler_getListingForRentalApplication,
  useCappsApplicationsWebApiHandler_getRentalApplication,
  useCappsRenterUserStatusesWebApiHandler_getUserStatuses,
} from '@zg-rentals/ts-capps-client-react-query';
import { toRentalApplication, toRentalApplicationListing } from '../utils/capps-mappers';
import { EMPTY_APPLICATION_LISTING } from '../contexts/ApplicationContext';

export const RENTAL_APPLICATION_TAG = 'rentalApplication';
export const LISTING_FOR_RENTAL_APPLICATION_TAG = 'listingForRentalApplication';
export const USER_STATUSES_TAG = 'userStatuses';

const LISTING_STALE_TIME_MS = 15 * 60 * 1000;

/**
 * Refetches the application and the user statuses, matching what the old
 * GetRentalApplication graphql query used to return in a single response
 */
export function invalidateRentalApplication(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    predicate: ({ meta }) =>
      (meta?.tags?.includes(RENTAL_APPLICATION_TAG) || meta?.tags?.includes(USER_STATUSES_TAG)) ?? false,
  });
}

export async function fetchLatestApplication(queryClient: QueryClient, applicationId: string) {
  const options = CappsApplicationsWebApiHandler_getRentalApplication_queryOptions({ id: applicationId });
  // fetchQuery joins a refetch in flight, which can return the data from before the last write
  await queryClient.cancelQueries({ queryKey: options.queryKey });
  const response = await queryClient.fetchQuery({ ...options, staleTime: 0, meta: { tags: [RENTAL_APPLICATION_TAG] } });
  return toRentalApplication(response);
}

export function useRentalApplication(applicationId: string | undefined) {
  const { data, isPending, isLoadingError, isFetching } = useCappsApplicationsWebApiHandler_getRentalApplication(
    { id: applicationId ?? '' },
    {
      enabled: !!applicationId,
      meta: { tags: [RENTAL_APPLICATION_TAG] },
    },
  );

  const application = useMemo(() => (data ? toRentalApplication(data) : undefined), [data]);

  return { application, isPending, isError: isLoadingError, isFetching };
}

export function useListingForRentalApplication(applicationId: string | undefined, { enabled = true } = {}) {
  const { data, isPending, isError } = useCappsApplicationsWebApiHandler_getListingForRentalApplication(
    { id: applicationId ?? '' },
    {
      enabled: enabled && !!applicationId,
      staleTime: LISTING_STALE_TIME_MS,
      meta: { tags: [LISTING_FOR_RENTAL_APPLICATION_TAG] },
    },
  );

  const listing = useMemo(() => (data ? toRentalApplicationListing(data) : undefined), [data]);

  return { listing, isPending, isError };
}

export function useRentalApplicationWithListing(applicationId: string | undefined) {
  const { application, isPending: applicationPending, isError: applicationError } = useRentalApplication(applicationId);
  const { listing, isPending: listingPending } = useListingForRentalApplication(applicationId);

  return {
    application,
    // A failed listing fetch renders the application without listing data instead of blocking it
    listing: listing ?? EMPTY_APPLICATION_LISTING,
    isPending: applicationPending || listingPending,
    isError: applicationError,
  };
}

export function useUserStatuses({ enabled = true }: { enabled?: boolean } = {}) {
  const { data, isPending, isError } = useCappsRenterUserStatusesWebApiHandler_getUserStatuses({
    enabled,
    meta: { tags: [USER_STATUSES_TAG] },
  });

  return { userStatuses: data?.userStatuses, isPending, isError };
}
